/**
 * Prerequisite Service
 * Consulta quais elos de uma cadeia de pré-requisitos já existem no escopo do usuário.
 * Chamado só quando uma listagem voltou vazia (#385).
 */

import apiClient from './apiClient';

/**
 * Mapa elo → existe. O backend devolve apenas os elos que o usuário pode ler:
 * chave ausente é elo invisível, e o front não distingue isso de "não existe".
 */
export type PrerequisiteNodes = Record<string, boolean>;

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/**
 * A rota responde `{ nodes }` no topo do corpo; o restante da API embrulha em `data`,
 * e o cliente aceita as duas formas para não depender de qual convenção prevaleceu.
 */
function extractNodes(payload: unknown): PrerequisiteNodes | null {
  const body = asRecord(payload);
  if (!body) return null;

  const raw = asRecord(body.nodes) ?? asRecord(asRecord(body.data)?.nodes);
  if (!raw) return null;

  const nodes: PrerequisiteNodes = {};
  for (const [node, exists] of Object.entries(raw)) {
    if (typeof exists === 'boolean') {
      nodes[node] = exists;
    }
  }
  return nodes;
}

class PrerequisiteService {
  private baseUrl = '/prerequisites';

  async getPrerequisites(nodes: string[]): Promise<PrerequisiteNodes> {
    const query = new URLSearchParams({ nodes: nodes.join(',') });
    const response = await apiClient.get<unknown>(`${this.baseUrl}?${query.toString()}`);

    const parsed = extractNodes(response);
    if (!parsed) {
      throw new Error('Resposta de pré-requisitos em formato inesperado');
    }
    return parsed;
  }
}

export const prerequisiteService = new PrerequisiteService();
export default prerequisiteService;
